import { endpoint, env, getAuthToken, handleUnauthorized } from '@eruvaka/web-pondlogs-base';
const api = (path) => endpoint(env.apiBaseUrl, path);
const handleError = async (res, fallback) => {
    if (res.status === 401) {
        handleUnauthorized();
        throw new Error('Unauthorized');
    }
    let message = fallback;
    try {
        const body = (await res.json());
        if (body.errors && body.errors.length > 0) {
            message = body.errors.map((e) => e.message).join(', ');
        }
        else {
            message = body.message ?? body.error ?? fallback;
        }
    }
    catch {
        /* ignore parse errors */
    }
    throw new Error(message);
};
const authFetch = async (url, options = {}) => {
    const token = getAuthToken();
    return fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });
};
const MODULE_PERMISSION_MAP = {
    schedules: {
        moduleName: 'Schedules Management',
        view: [
            'VIEW_POND_GUARDS',
            'VIEW_SCHEDULES',
            'VIEW_PONDS',
            'VIEW_MANUAL_FEED',
            'VIEW_RESOURCES',
            'VIEW_GATEWAYS',
            'VIEW_POND_MOTHERS',
            'VIEW_SHRIMP_TALKS',
            'VIEW_CULTURE',
        ],
        update: [
            'UPDATE_SCHEDULES',
            'UPDATE_SHRIMP_TALKS',
            'UPDATE_POND_MOTHERS',
            'UPDATE_GATEWAYS',
            'UPDATE_MANUAL_FEED',
        ],
        delete: ['DELETE_SCHEDULES'],
    },
    devices: {
        moduleName: 'Device Management',
        view: [
            'VIEW_POND_GUARDS',
            'VIEW_POND_MOTHERS',
            'VIEW_SHRIMP_TALKS',
            'VIEW_GATEWAYS',
            'VIEW_POND_ALERTS',
            'VIEW_PONDS',
            'VIEW_WATER_QUALITY_SUMMARY',
            'VIEW_MANUAL_FEED',
            'VIEW_SCHEDULES',
            'VIEW_RESOURCES',
        ],
        update: [
            'UPDATE_POND_GUARDS',
            'UPDATE_POND_MOTHERS',
            'UPDATE_SHRIMP_TALKS',
            'UPDATE_GATEWAYS',
            'UPDATE_MANUAL_FEED',
        ],
        delete: ['DELETE_POND_GUARDS', 'DELETE_POND_MOTHERS', 'DELETE_SHRIMP_TALKS'],
    },
    sampling: {
        moduleName: 'Manage Sampling',
        view: ['VIEW_POND_SUMMARY', 'VIEW_PONDS', 'VIEW_ABW', 'VIEW_CULTURE'],
        update: ['UPDATE_ABW'],
        delete: ['DELETE_ABW'],
    },
    harvest: {
        moduleName: 'Harvest Management',
        view: ['VIEW_POND_SUMMARY', 'VIEW_PONDS', 'VIEW_ABW', 'VIEW_HARVESTS'],
        update: ['UPDATE_HARVESTS'],
        delete: ['DELETE_HARVESTS'],
    },
    inputs: {
        moduleName: 'Manage Inputs',
        view: ['VIEW_PONDS', 'VIEW_MEDICINES', 'VIEW_RESOURCES'],
        update: ['UPDATE_MEDICINES', 'UPDATE_RESOURCES'],
        delete: ['DELETE_MEDICINES', 'DELETE_RESOURCES'],
    },
    labTests: {
        moduleName: 'Manage Lab Tests',
        view: ['VIEW_LAB_TEST', 'VIEW_LABS', 'VIEW_PONDS'],
        update: ['UPDATE_LAB_TEST', 'UPDATE_LABS'],
        delete: ['DELETE_LAB_TEST', 'DELETE_LABS'],
    },
    inventory: {
        moduleName: 'Manage Inventory',
        view: ['VIEW_STOCK', 'VIEW_RESOURCES'],
        update: ['UPDATE_STOCK', 'UPDATE_RESOURCES'],
        delete: ['DELETE_STOCK', 'DELETE_RESOURCES'],
    },
};
const permissionsToAssignedModules = (permissions) => {
    const set = new Set(permissions ?? []);
    return Object.entries(MODULE_PERMISSION_MAP).reduce((acc, [moduleKey, map]) => {
        const view = map.view.length > 0 && map.view.every((permission) => set.has(permission));
        const update = map.update.length > 0 && map.update.every((permission) => set.has(permission));
        const del = map.delete.length > 0 && map.delete.every((permission) => set.has(permission));
        if (view || update || del) {
            acc.push({
                moduleKey,
                moduleName: map.moduleName,
                permissions: { view, update, delete: del },
            });
        }
        return acc;
    }, []);
};
const mapRole = (userType) => {
    const type = (userType ?? '').toUpperCase();
    if (type === 'ADMIN')
        return 'admin';
    if (type === 'POND_OWNER')
        return 'owner';
    if (type === 'POND_MANAGER')
        return 'manager';
    if (type === 'POND_WORKER')
        return 'worker';
    if (type === 'POND_TECHNICIAN')
        return 'technician';
    if (type === 'NO_ACCESS')
        return 'no_access';
    // Backward compatibility with interim V3 role
    if (type === 'READ_ONLY')
        return 'technician';
    return 'technician';
};
const mapUser = (u) => ({
    id: String(u._id),
    firstName: u.firstName ?? u.first_name ?? '',
    lastName: u.lastName ?? u.last_name ?? '',
    fullName: `${u.firstName ?? u.first_name ?? ''} ${u.lastName ?? u.last_name ?? ''}`.trim(),
    email: u.email ?? '',
    phone: u.phoneNumber ?? u.phone ?? u.mobile ?? '',
    role: mapRole(u.userType ?? u.user_type ?? ''),
    status: typeof u.isActive === 'boolean'
        ? u.isActive
            ? 'active'
            : 'inactive'
        : (u.status ?? '').toUpperCase() === 'ACTIVE'
            ? 'active'
            : 'inactive',
    assignedLocations: u.assignedLocations?.map((l) => ({ id: String(l._id), name: l.name })) ??
        u.allowed_locations?.map((id) => ({ id: String(id), name: '' })),
    assignedModules: u.assignedModules ?? permissionsToAssignedModules(u.permissions),
    isEmailVerified: u.isEmailVerified ?? false,
    isPhoneVerified: u.isPhoneVerified ?? false,
    createdAt: u.createdAt ?? u.created_at ?? '',
    updatedAt: u.updatedAt ?? u.updated_at ?? '',
});
const mapRoleToUserType = (role) => {
    if (role === 'admin')
        return 'ADMIN';
    if (role === 'owner')
        return 'POND_OWNER';
    if (role === 'manager')
        return 'POND_MANAGER';
    if (role === 'worker')
        return 'POND_WORKER';
    if (role === 'technician')
        return 'POND_TECHNICIAN';
    if (role === 'no_access')
        return 'NO_ACCESS';
    return 'POND_TECHNICIAN';
};
/**
 * Converts module-based permissions (UI format) to a flat legacy permission
 * string array (e.g. ['VIEW_PONDS', 'UPDATE_SCHEDULES', ...]).
 */
const assignedModulesToPermissions = (modules) => {
    const result = new Set();
    for (const module of modules) {
        const map = MODULE_PERMISSION_MAP[module.moduleKey];
        if (!map)
            continue;
        if (module.permissions.view)
            map.view.forEach((p) => result.add(p));
        if (module.permissions.update)
            map.update.forEach((p) => result.add(p));
        if (module.permissions.delete)
            map.delete.forEach((p) => result.add(p));
    }
    return Array.from(result);
};
export const fetchSubUsers = async (locationId, userType) => {
    const params = new URLSearchParams({ get_all: 'true' });
    if (locationId)
        params.set('location_id', locationId);
    if (userType)
        params.set('user_type', userType);
    const res = await authFetch(api(`/api/v3/users/sub-users?${params.toString()}`));
    if (!res.ok)
        return handleError(res, 'Failed to fetch users');
    const body = (await res.json());
    const users = body.sub_users ?? body.data ?? [];
    return users.map(mapUser);
};
export const fetchSubUsersGrouped = async (locationId, userType) => {
    const params = new URLSearchParams({ get_all: 'true' });
    if (locationId)
        params.set('location_id', locationId);
    if (userType)
        params.set('user_type', userType);
    const res = await authFetch(api(`/api/v3/users/sub-users?${params.toString()}`));
    if (!res.ok)
        return handleError(res, 'Failed to fetch users');
    const body = (await res.json());
    const users = body.sub_users ?? body.data ?? [];
    const skrettingTechs = body.skretting_technicians ?? [];
    return {
        subUsers: users.map(mapUser),
        skrettingTechnicians: skrettingTechs.map(mapUser),
    };
};
export const createSubUser = async (payload) => {
    // Build flat permissions from module-level permissions when provided
    const permissions = payload.assignedModules && payload.assignedModules.length > 0
        ? assignedModulesToPermissions(payload.assignedModules)
        : undefined;
    // Send the legacy snake_case format expected by POST /users/sub-users
    const body = {
        first_name: payload.firstName,
        last_name: payload.lastName,
        ...(payload.email ? { email: payload.email } : {}),
        ...(payload.phone ? { mobile: payload.phone } : {}),
        ...(payload.password ? { password: payload.password } : {}),
        user_type: mapRoleToUserType(payload.role),
        ...(payload.countryCode ? { country: payload.countryCode } : {}),
        ...(payload.assignedLocations ? { allowed_locations: payload.assignedLocations } : {}),
        ...(permissions ? { permissions } : {}),
    };
    const res = await authFetch(api('/api/v3/users/sub-users'), {
        method: 'POST',
        body: JSON.stringify(body),
    });
    if (!res.ok)
        return handleError(res, 'Failed to create user');
    const data = (await res.json());
    return mapUser(data.user_details);
};
export const updateSubUser = async (userId, payload) => {
    const body = {};
    if (payload.firstName !== undefined)
        body.firstName = payload.firstName;
    if (payload.lastName !== undefined)
        body.lastName = payload.lastName;
    if (payload.email !== undefined)
        body.email = payload.email;
    if (payload.phone !== undefined)
        body.phoneNumber = payload.phone;
    if (payload.role !== undefined)
        body.userType = mapRoleToUserType(payload.role);
    if (payload.status !== undefined)
        body.isActive = payload.status === 'active';
    if (payload.assignedLocations !== undefined)
        body.allowed_locations = payload.assignedLocations;
    if (payload.assignedModules !== undefined)
        body.assignedModules = payload.assignedModules;
    const res = await authFetch(api(`/api/v3/users/${userId}`), {
        method: 'PUT',
        body: JSON.stringify(body),
    });
    if (!res.ok)
        return handleError(res, 'Failed to update user');
    const data = (await res.json());
    return mapUser(data.data);
};
export const updateSubUserWithMessage = async (userId, payload) => {
    const body = {};
    if (payload.firstName !== undefined)
        body.firstName = payload.firstName;
    if (payload.lastName !== undefined)
        body.lastName = payload.lastName;
    if (payload.email !== undefined)
        body.email = payload.email;
    if (payload.phone !== undefined)
        body.phoneNumber = payload.phone;
    if (payload.role !== undefined)
        body.userType = mapRoleToUserType(payload.role);
    if (payload.status !== undefined)
        body.isActive = payload.status === 'active';
    if (payload.assignedLocations !== undefined)
        body.allowed_locations = payload.assignedLocations;
    if (payload.assignedModules !== undefined)
        body.assignedModules = payload.assignedModules;
    const res = await authFetch(api(`/api/v3/users/${userId}`), {
        method: 'PUT',
        body: JSON.stringify(body),
    });
    if (!res.ok)
        return handleError(res, 'Failed to update user');
    const data = (await res.json());
    return {
        user: mapUser(data.data),
        message: data.message,
    };
};
export const deleteSubUser = async (userId) => {
    const res = await authFetch(api(`/api/v3/users/${userId}`), {
        method: 'DELETE',
        body: JSON.stringify({ confirmation: 'Delete Account' }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to delete user');
};
export const updateSubUserPassword = async (userId, currentPassword, newPassword, confirmPassword) => {
    const res = await authFetch(api(`/api/v3/users/${userId}/password`), {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to update password');
};
export const updateSubUserPermissions = async (userId, assignedModules) => {
    const res = await authFetch(api(`/api/v3/users/${userId}/permissions`), {
        method: 'PATCH',
        body: JSON.stringify({ assignedModules }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to update permissions');
    const data = (await res.json());
    return mapUser(data.data);
};
export const uploadSubUserPhoto = async (userId, file) => {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('avatar', file);
    const res = await fetch(api(`/api/v3/users/${userId}/avatar`), {
        method: 'POST',
        credentials: 'include',
        // Do NOT set Content-Type — browser sets it with the multipart boundary
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
    });
    if (!res.ok)
        return handleError(res, 'Failed to upload profile photo');
};
export const initiateSubUserEmailChange = async (userId, newEmail) => {
    const res = await authFetch(api(`/api/v3/users/${userId}/email`), {
        method: 'POST',
        body: JSON.stringify({ newEmail }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to initiate email change');
};
export const verifySubUserEmailChange = async (userId, otp) => {
    const res = await authFetch(api(`/api/v3/users/${userId}/email/verify`), {
        method: 'POST',
        body: JSON.stringify({ otp }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to verify email change');
};
export const initiateSubUserPhoneChange = async (userId, newPhone) => {
    const res = await authFetch(api(`/api/v3/users/${userId}/phone`), {
        method: 'POST',
        body: JSON.stringify({ newPhone }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to initiate phone change');
};
export const verifySubUserPhoneChange = async (userId, otp) => {
    const res = await authFetch(api(`/api/v3/users/${userId}/phone/verify`), {
        method: 'POST',
        body: JSON.stringify({ otp }),
    });
    if (!res.ok)
        return handleError(res, 'Failed to verify phone change');
};
